import { useCallback, useEffect, useRef, useState } from "react";

import { supportsPopover as supportsNativePopover } from "~/helpers/supports-popover";

type SearchPopoverViewport = "desktop" | "mobile";

interface UseSearchPopoverProps {
  breakpoint: string;
  handleSearchClose?: () => void;
  handleSearchOpen?: () => void;
  isDesktopView: boolean;
  isSearchOpen?: boolean;
  viewport: SearchPopoverViewport;
}

export const useSearchPopover = <T extends HTMLElement = HTMLDivElement>({
  breakpoint,
  handleSearchClose,
  handleSearchOpen,
  isDesktopView,
  isSearchOpen,
  viewport,
}: UseSearchPopoverProps) => {
  const popoverRef = useRef<T>(null);
  const hasSynchronizedInitialState = useRef(false);
  const synchronizedSearchOpenState = useRef(Boolean(isSearchOpen));
  const [supportsPopover, setSupportsPopover] = useState<boolean>();

  useEffect(() => {
    setSupportsPopover(supportsNativePopover());
  }, []);

  useEffect(() => {
    synchronizedSearchOpenState.current = Boolean(isSearchOpen);
  }, [isSearchOpen]);

  const synchronizeSearchOpenState = useCallback(
    (nextIsSearchOpen: boolean) => {
      if (synchronizedSearchOpenState.current === nextIsSearchOpen) {
        return;
      }

      synchronizedSearchOpenState.current = nextIsSearchOpen;

      if (nextIsSearchOpen) {
        handleSearchOpen?.();
      } else {
        handleSearchClose?.();
      }
    },
    [handleSearchClose, handleSearchOpen],
  );

  const isActiveViewport = useCallback(() => {
    const isDesktop =
      typeof globalThis.matchMedia === "function"
        ? globalThis.matchMedia(breakpoint.replace(/^@media\s*/, "")).matches
        : isDesktopView;

    return viewport === "desktop" ? isDesktop : !isDesktop;
  }, [breakpoint, isDesktopView, viewport]);

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover || !supportsPopover || !isActiveViewport()) {
      return;
    }

    const isPopoverOpen = popover.matches(":popover-open");

    if (!hasSynchronizedInitialState.current) {
      hasSynchronizedInitialState.current = true;
      if (isPopoverOpen) {
        synchronizeSearchOpenState(true);
        return;
      }
    }

    if (isSearchOpen && !isPopoverOpen) {
      popover.showPopover();
    } else if (!isSearchOpen && isPopoverOpen) {
      popover.hidePopover();
    }
  }, [isActiveViewport, isSearchOpen, supportsPopover, synchronizeSearchOpenState]);

  const handleToggle = useCallback(
    (event: React.ToggleEvent<T>) => {
      if (!isActiveViewport()) {
        return;
      }

      synchronizeSearchOpenState(event.newState === "open");
    },
    [isActiveViewport, synchronizeSearchOpenState],
  );

  return {
    handleToggle,
    popoverRef,
    supportsPopover,
  };
};
